import React, {FC, useEffect, useMemo, useState} from 'react'
import {Headers, SortKeys, SortOrder} from '../types'
import {
    WebFormBgStatusMap,
    WebFormsDataTable,
    WebFormsDashboardData, WebFormStatus, formSelectedType,
} from '../../../../../app/@types'
import {THead} from '../components/THead'
import {Pagination} from '../../../content/pagination/Pagination'
import {paginate} from '../../../content/pagination/types'
import {useSearchedData, useSortedData} from '../../../../../app/features'
import {useForms} from '../../../../../app/@contexts'
import {TableContent} from './TableContent'
import {FormSelectionAndExport, SearchAndFilter} from './layout'

const headers_wf: Headers<WebFormsDataTable>[] = [
    {key: 'date', label: 'Date', className: 'fw-bolder text-muted bg-light min-w-150px'},
    {key: 'title', label: 'Formulaires', className: 'fw-bolder text-muted bg-light min-w-150px'},
    {key: 'lastName', label: 'Nom', className: 'fw-bolder text-muted bg-light min-w-150px'},
    {key: 'firstName', label: 'Prénom', className: 'fw-bolder text-muted bg-light min-w-150px'},
    {key: 'status', label: 'Statut', className: 'fw-bolder text-muted bg-light min-w-125px'},
    {key: 'name_categories', label: 'Catégories', className: 'fw-bolder text-muted bg-light min-w-125px'},
]

const headers_wp: Headers<WebFormsDataTable>[] = [
    {key: 'date', label: 'Date', className: 'fw-bolder text-muted bg-light min-w-150px'},
    {key: 'title', label: 'Formulaires', className: 'fw-bolder text-muted bg-light min-w-150px'},
    {key: 'firstName', label: 'Première champs', className: 'fw-bolder text-muted bg-light min-w-150px'},
    {key: 'lastName', label: 'Deuxième champs', className: 'fw-bolder text-muted bg-light min-w-150px'},
    {key: 'status', label: 'Statut', className: 'fw-bolder text-muted bg-light min-w-125px'},
    {key: 'name_categories', label: 'Catégories', className: 'fw-bolder text-muted bg-light min-w-125px'},
]

type Props = {
    data: WebFormsDashboardData
}

function filterByDataFrom(dataTable: WebFormsDataTable[], type: 'wp' | 'wf'): WebFormsDataTable[] {

    return dataTable.filter(item => item.data_from === type)
}

const TablesWidget14: FC<Props> = ({data}) => {

    const {setAllStatus, allStatus} = useForms()

    const [sortKey, setSortKey] = useState<SortKeys<WebFormsDataTable>>('date')
    const [sortOrder, setSortOrder] = useState<SortOrder>('desc')
    const [currentPage, setCurrentPage] = useState(1)
    const [itemsPerPage, setItemsPerPage] = useState(10)
    const [dataFiltered, setDataFiltered] = useState<WebFormsDataTable[]>([])
    const [categoriesRow, setCategoriesRow] = useState('all')
    const [statusRow, setStatusRow] = useState('all')
    const [uniqueForm, setUniqueForm] = useState<WebFormsDataTable[]>([])
    // const [formSelected, setFormSelected] = useState<string>("")
    const [formSelected, setFormSelected] = useState<formSelectedType>({
        selected: '',
        params: {type: ''},
        formId: '',
    })
    const [formType, setFormType] = useState<'wp' | 'wf'>('wp')

    const [dataTable, setDataTable] = useState(data.dataTable)

    const paginateData = useMemo(
        () => paginate<WebFormsDataTable>({currentPage, itemsPerPage, data: dataFiltered}),
        [currentPage, itemsPerPage, dataFiltered],
    )

    useEffect(() => {

        const {status, dataTable} = data
        const wpItems = filterByDataFrom(dataTable, 'wp')
        const wfItems = filterByDataFrom(dataTable, 'wf')

        const dataTable_ = wpItems.length ? wpItems : wfItems

        setFormType(wpItems.length ? 'wp' : 'wf')

        const updateDataTable = dataTable_.map(item => {

            const obj = JSON.parse(item.data)
            const modifiedObject = Object.keys(obj).reduce((acc: {[key: string]: any}, key: string) => {

                const newKey: string = key.split('//')[0]
                acc[newKey] = obj[key]
                return acc
            }, {})


            const modifiedDataObjectValues = Object.values(obj).map(i => i)

            const {rue, rue_2, ville, region, code_postal, pays} = modifiedObject

            const street = [
                rue ?? '',
                rue_2 ?? '',
                ville ?? '',
                region ?? '',
                code_postal ?? '',
                pays ?? '',
            ].filter(Boolean).join(' ')

            let firstName: string = ''
            let lastName: string = ''

            if (item.data_from === 'wf') {
                firstName = modifiedObject.prenom || ''
                lastName = modifiedObject.nom || ''
            } else if (item.data_from === 'wp') {
                [firstName, lastName] = modifiedDataObjectValues
                    .toString()
                    .split(',')
                    .slice(0, 2)
                    .map(value => value.trim())
            }

            return {
                ...item,
                firstName: firstName || '',
                lastName: lastName || '',
                email: modifiedObject.email,
                telephone: modifiedObject.telephone,
                societe: modifiedObject.societe,
                street: street.trim() || '',
                data: modifiedDataObjectValues.toString(),
            }
        })

        setDataTable(updateDataTable)

        const statusesWithBg: WebFormStatus[] = status.map(s => ({
            ...s,
            bg: WebFormBgStatusMap.get(`${s.id}`) || '',
        }))

        setAllStatus(statusesWithBg)

        const set = new Set()
        const unique = dataTable_.filter(item => {
            const alreadyHas = set.has(item.title)
            set.add(item.title)
            return !alreadyHas
        }).sort((a, b) => a.title.localeCompare(b.title))

        setUniqueForm(unique)
    }, [data])

    const dataSorted: WebFormsDataTable[] = useSortedData({
        data: dataTable, sortKey, reverse: sortOrder === 'desc',
    })

    const {searchQuery, setSearchQuery} = useSearchedData<WebFormsDataTable>({
        data: dataSorted,
    })

    useEffect(() => {

        const filteredData = dataSorted.filter((item) => {

            const matchesStatus = statusRow === 'all' || item.status === statusRow
            const matchesCategory = categoriesRow === 'all' || item.name_categories === categoriesRow

            // Convert the fields to lowercase strings for comparison with search input
            const searchValue = searchQuery.toLowerCase()

            const matchesCreatedAt = item.created_at?.toLowerCase().includes(searchValue)
            const matchesTitle = item.title.toLowerCase().includes(searchValue)
            const matchesData = item.data.toLowerCase().includes(searchValue)
            const matchesSearch = matchesCreatedAt || matchesTitle || matchesData

            // const matchesSearch = item.title.toLowerCase().includes(searchInput.toLowerCase())
            // const matchesForm = formSelected <= 0 || item.form_id === formSelected
            const matchesForm = formSelected.selected === "" || item.title === formSelected.selected
            return matchesForm && matchesStatus && matchesCategory && matchesSearch
        })
        setDataFiltered(filteredData)
    }, [statusRow, categoriesRow, searchQuery, formSelected, dataTable, dataSorted])

    return (
        <>

            <FormSelectionAndExport uniqueForm={uniqueForm}
                                    formSelected={formSelected}
                                    setFormSelected={setFormSelected}
            />

            <SearchAndFilter setSearchQuery={setSearchQuery}
                             allStatus={allStatus}
                             categories={data.categorie}
                             setStatusRow={setStatusRow}
                             setCategoriesRow={setCategoriesRow}
                             formType={formType}
            />

            <TableContent TableHead={
                <THead headers={formType === 'wf' ? headers_wf : headers_wp}
                       sortKey={sortKey}
                       setSortKey={setSortKey}
                       sortOrder={sortOrder}
                       setSortOrder={setSortOrder}
                       children={
                           <>
                               <th className="fw-bolder text-muted bg-light min-w-50px">
                                   <span
                                       className="btn btn-sm btn-icon w-auto px-0 text-muted cursor-default">Description</span>
                               </th>
                               <th className="fw-bolder text-muted bg-light min-w-50px" />
                           </>
                       }
                />
            } paginateData={paginateData} allStatus={allStatus}
            />

            <Pagination itemsPerPage={itemsPerPage}
                        totalItems={dataFiltered.length}
                        setCurrentPage={setCurrentPage}
                        setItemsPerPage={setItemsPerPage}
            />
        </>
    )
}

export {TablesWidget14}